export type Page = Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">;

export interface Poll {
  now: () => void;
  stop: () => void;
}

/** Start `load` immediately and again `ms` after each `load` ends. Skip it while the user cannot see the page. */
export function startPoll(load: (signal: AbortSignal) => Promise<unknown>, ms: number, page: Page = document): Poll {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let running: AbortController | null = null;
  let settled = 0;
  let stopped = false;

  const run = () => {
    clearTimeout(timer);
    timer = undefined;
    running?.abort();
    const control = new AbortController();
    running = control;
    void load(control.signal)
      .catch(() => undefined)
      .finally(() => {
        if (running !== control) return;
        running = null;
        settled = Date.now();
        if (!stopped) timer = setTimeout(tick, ms);
      });
  };
  const tick = () => {
    timer = undefined;
    if (page.visibilityState !== "hidden") run();
  };
  const shown = () => {
    if (stopped || running || page.visibilityState !== "visible") return;
    if (Date.now() - settled >= ms) run();
  };

  page.addEventListener("visibilitychange", shown);
  run();
  return {
    now: () => {
      if (!stopped) run();
    },
    stop: () => {
      stopped = true;
      clearTimeout(timer);
      running?.abort();
      running = null;
      page.removeEventListener("visibilitychange", shown);
    },
  };
}
